import type { Entity, Link } from '@/types/entity';
import type { DerivedGraph } from './graph';
import { derivedPartners } from './graph';
import { deriveAxisModel } from './thesis';
import { readAxis, readNumber } from './fields';

/**
 * 비트 맥락 번들 (레이어 A) — 선택 비트가 *이미 묶고 있는* 사전설정(캐릭터 OS·세계규칙·플롯·테마)을
 * 데이터에서 결정적으로 파생. derive-don't-declare: 비트 필드/링크가 바뀌면 번들이 자동 재계산.
 * 순수함수(entities·links·beat) → 번들. 훅(useBeatContext)은 안정 store ref 위 단일 useMemo로만 호출(#185).
 * codex 검토 반영: castId별 companions 집계·active/type 가드·결정적 정렬·prev/next plotColumns fallback·registerColor=Entity.color.
 */

export interface BeatRef {
  id: string;
  name: string;
  summary?: string;
  goal?: string;
}
export interface EntityRef {
  id: string;
  name: string;
}
export interface CastMember {
  id: string;
  name: string;
  tier?: string;
  registerColor?: string; // Entity.color (레지스터 톤 단서; canon ac→color로 저장됨)
  line?: string;
  wound?: string;
  lie?: string;
  desire?: string;
  need?: string;
  flaw?: string;
  arc?: string;
  source: 'field' | 'mention'; // 주연(beat.fields.characters) vs 배경 멘션(refs)
}
export interface SceneWorld {
  locationId?: string;
  locationName?: string;
  locationDesc?: string;
  planetId?: string;
  planetName?: string;
  philosophy?: string;
  culture?: string;
  env?: string;
  role?: string;
  axis?: number; // 0(분석)~1(합일)
}
export interface PlotPosition {
  storylineId?: string;
  storylineName?: string;
  storylineColor?: string;
  storylineLead?: EntityRef | null; // storyline.fields.lead = 대표 인물
  causesIn: BeatRef[]; // 상류 원인(CAUSAL l.to===beat)
  causesOut: BeatRef[]; // 하류 결과(CAUSAL l.from===beat)
  prevInLine: BeatRef | null;
  nextInLine: BeatRef | null;
}
export interface MirrorRef {
  aId: string;
  aName: string;
  bId: string;
  bName: string;
  label?: string;
}
export interface ThemeContext {
  sceneAxis: number | null;
  leaning: 'analysis' | 'union' | 'balanced' | null;
  centroid: number | null;
  mirrors: MirrorRef[];
}
export interface Companion {
  id: string;
  name: string;
  count: number; // 동시등장 강도 — cast 멤버들과의 cooccur 쌍 합산(distinct 장면 수가 아님, 랭킹 신호)
}
export interface BeatContext {
  beatId: string;
  cast: CastMember[];
  scene: SceneWorld | null;
  plot: PlotPosition;
  theme: ThemeContext;
  companions: Companion[];
}

export interface BeatContextOpts {
  colOrder?: string[]; // project.plotColumns id 순서 → 같은 storyline 전후 정렬 rank
}

type EMap = Record<string, Entity>;

const str = (v: unknown): string | undefined => (typeof v === 'string' && v.trim() !== '' ? v : undefined);

/** active + (선택)type 일치 엔티티만. 삭제·미존재·타입불일치는 undefined → 슬롯에서 skip. */
function activeOf(emap: EMap, id: string | undefined, type?: Entity['type']): Entity | undefined {
  if (!id) return undefined;
  const e = emap[id];
  if (!e || e.deletedAt) return undefined;
  if (type && e.type !== type) return undefined;
  return e;
}

/** 플롯 좌표 rank — colOrder 인덱스(미지정 col=마지막 bucket), finite order, id tiebreak. seed 밖 비트도 안전. */
function plotRank(e: Entity, colOrder?: string[]): { col: number; order: number } {
  const col = str(e.fields.col) ?? '';
  const ci = colOrder ? colOrder.indexOf(col) : -1;
  return {
    col: ci >= 0 ? ci : Number.MAX_SAFE_INTEGER,
    order: readNumber(e, 'order') ?? Number.MAX_SAFE_INTEGER,
  };
}
const byPlotPos = (colOrder?: string[]) => (a: Entity, b: Entity) => {
  const ra = plotRank(a, colOrder);
  const rb = plotRank(b, colOrder);
  return ra.col - rb.col || ra.order - rb.order || (a.id < b.id ? -1 : 1);
};

function toBeatRef(e: Entity): BeatRef {
  return { id: e.id, name: e.name, summary: str(e.summary) ?? str(e.fields.summary), goal: str(e.fields.goal) };
}

function deriveCast(beat: Entity, emap: EMap): { cast: CastMember[]; castIds: Set<string> } {
  const cast: CastMember[] = [];
  const castIds = new Set<string>();
  const push = (id: unknown, source: 'field' | 'mention') => {
    if (typeof id !== 'string' || castIds.has(id)) return;
    const e = activeOf(emap, id, 'character');
    if (!e) return;
    castIds.add(id);
    cast.push({
      id: e.id,
      name: e.name,
      tier: str(e.fields.tier),
      registerColor: e.color,
      line: str(e.fields.line),
      wound: str(e.fields.wound),
      lie: str(e.fields.lie),
      desire: str(e.fields.desire),
      need: str(e.fields.need),
      flaw: str(e.fields.flaw),
      arc: str(e.fields.arc),
      source,
    });
  };
  if (Array.isArray(beat.fields.characters)) for (const id of beat.fields.characters as unknown[]) push(id, 'field');
  for (const r of beat.refs ?? []) push(r, 'mention');
  return { cast, castIds };
}

function deriveScene(beat: Entity, emap: EMap): SceneWorld | null {
  const e = activeOf(emap, str(beat.fields.location));
  if (!e) return null;
  const planetFields = (p: Entity) => ({
    planetId: p.id,
    planetName: p.name,
    philosophy: str(p.fields.philosophy),
    culture: str(p.fields.culture),
    env: str(p.fields.env),
    role: str(p.fields.role),
    axis: readAxis(p),
  });
  if (e.type === 'planet') return planetFields(e);
  if (e.type === 'location') {
    const scene: SceneWorld = { locationId: e.id, locationName: e.name, locationDesc: str(e.fields.desc) };
    const planet = activeOf(emap, str(e.fields.parent), 'planet'); // seed: location.fields.parent = 행성 id
    if (planet) Object.assign(scene, planetFields(planet));
    return scene;
  }
  return null;
}

function derivePlot(beat: Entity, emap: EMap, links: Link[], colOrder?: string[]): PlotPosition {
  const storyline = activeOf(emap, str(beat.fields.storylineId), 'storyline');
  const leadE = storyline ? activeOf(emap, str(storyline.fields.lead), 'character') : undefined;

  const inIds = new Set<string>();
  const outIds = new Set<string>();
  for (const l of links) {
    if (l.deletedAt || l.type !== 'related' || l.label !== '인과' || !l.directed) continue;
    if (l.to === beat.id) inIds.add(l.from);
    if (l.from === beat.id) outIds.add(l.to);
  }
  const refsOf = (ids: Set<string>): BeatRef[] => {
    const ents = [...ids].map((id) => activeOf(emap, id, 'beat')).filter((e): e is Entity => !!e);
    ents.sort(byPlotPos(colOrder));
    return ents.map(toBeatRef);
  };

  let prevInLine: BeatRef | null = null;
  let nextInLine: BeatRef | null = null;
  const storylineId = storyline?.id;
  if (storylineId) {
    const line = Object.values(emap)
      .filter((e) => !e.deletedAt && e.type === 'beat' && str(e.fields.storylineId) === storylineId)
      .sort(byPlotPos(colOrder));
    const idx = line.findIndex((b) => b.id === beat.id);
    if (idx >= 0) {
      prevInLine = idx > 0 ? toBeatRef(line[idx - 1]!) : null;
      nextInLine = idx < line.length - 1 ? toBeatRef(line[idx + 1]!) : null;
    }
  }
  return {
    storylineId,
    storylineName: storyline?.name,
    storylineColor: storyline?.color,
    storylineLead: leadE ? { id: leadE.id, name: leadE.name } : null,
    causesIn: refsOf(inIds),
    causesOut: refsOf(outIds),
    prevInLine,
    nextInLine,
  };
}

function deriveMirrors(links: Link[], emap: EMap, touch: Set<string>): MirrorRef[] {
  const out: MirrorRef[] = [];
  const seen = new Set<string>();
  for (const l of links) {
    if (l.deletedAt || l.type !== 'mirror' || l.from === l.to) continue;
    if (!touch.has(l.from) && !touch.has(l.to)) continue;
    const a = activeOf(emap, l.from);
    const b = activeOf(emap, l.to);
    if (!a || !b) continue;
    const key = [l.from, l.to].sort().join(' ');
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ aId: a.id, aName: a.name, bId: b.id, bName: b.name, label: str(l.label) });
  }
  out.sort((x, y) => (`${x.aId} ${x.bId}` < `${y.aId} ${y.bId}` ? -1 : 1));
  return out;
}

/** companions — castId별 cooccur 파트너 집계(sum). cast 자신·non-character·삭제 제외. count desc·id asc top-5. */
function deriveCompanions(graph: DerivedGraph, emap: EMap, castIds: Set<string>, beatId: string): Companion[] {
  const agg = new Map<string, number>();
  for (const cid of castIds) {
    for (const p of derivedPartners(graph, cid, castIds)) {
      if (p.id === beatId || castIds.has(p.id)) continue;
      if (!activeOf(emap, p.id, 'character')) continue;
      agg.set(p.id, (agg.get(p.id) ?? 0) + p.count);
    }
  }
  return [...agg.entries()]
    .map(([id, count]) => ({ id, name: emap[id]?.name ?? id, count }))
    .sort((a, b) => b.count - a.count || (a.id < b.id ? -1 : 1))
    .slice(0, 5);
}

export function deriveBeatContext(
  beat: Entity,
  emap: EMap,
  links: Link[],
  graph: DerivedGraph,
  opts: BeatContextOpts = {},
): BeatContext {
  const { cast, castIds } = deriveCast(beat, emap);
  const scene = deriveScene(beat, emap);

  const planets = Object.values(emap).filter((e) => !e.deletedAt && e.type === 'planet');
  const axisModel = deriveAxisModel(planets);
  const touch = new Set(castIds);
  if (scene?.planetId) touch.add(scene.planetId);

  return {
    beatId: beat.id,
    cast,
    scene,
    plot: derivePlot(beat, emap, links, opts.colOrder),
    theme: {
      sceneAxis: scene?.axis ?? null,
      leaning: axisModel.leaning,
      centroid: axisModel.centroid,
      mirrors: deriveMirrors(links, emap, touch),
    },
    companions: deriveCompanions(graph, emap, castIds, beat.id),
  };
}
